/**
 * ActionBar — Sprint 2 shared primitive.
 *
 * Footer-bar renderer for approval card actions declared in
 * content_schema.actions. Each Action carries:
 *   - `decision`: 'approve' | 'reject' | 'custom' — dispatched to the parent
 *   - `variant`: visual style mapped to shadcn Button variants
 *   - `shortcut`: optional keyboard accelerator (e.g. "mod+enter")
 *   - `confirmation`: optional confirm dialog before firing
 *
 * The keyboard listener is bound document-wide while the bar is mounted, so
 * a user can press the shortcut anywhere on the page (matching the Gmail /
 * Outlook / Linear keyboard ergonomic the spec calls out in §8.4).
 *
 * Authority: docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.4 ("Actions in fixed
 * footer bar … Keyboard shortcuts wired per action's `shortcut`").
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { Action } from '@argusai/shared';

interface ActionBarProps {
  actions: Action[];
  approvalId: string;
  onDecide: (action: Action) => void | Promise<void>;
  /** Disables every action — used while a decision is in flight. */
  disabled?: boolean;
  className?: string;
}

// Map ActionVariant → shadcn Button variant.
function buttonVariantFor(v: Action['variant']) {
  switch (v) {
    case 'primary':
      return 'default' as const;
    case 'danger':
      return 'destructive' as const;
    case 'ghost':
      return 'ghost' as const;
    case 'secondary':
    default:
      return 'outline' as const;
  }
}

/**
 * Parse a shortcut string like "mod+enter", "cmd+enter", "ctrl+s", "esc"
 * into an event matcher. We accept "mod" + "cmd" interchangeably (mod
 * maps to metaKey on macOS, ctrlKey elsewhere). Case-insensitive.
 */
function matchShortcut(shortcut: string, e: KeyboardEvent): boolean {
  const parts = shortcut.toLowerCase().split('+').map((p) => p.trim());
  const key = parts[parts.length - 1];
  const mods = new Set(parts.slice(0, -1));
  const wantCtrl = mods.has('ctrl');
  const wantMeta = mods.has('cmd') || mods.has('meta');
  // `mod` follows the platform — meta on macOS, ctrl elsewhere.
  const wantMod = mods.has('mod');
  const wantShift = mods.has('shift');
  const wantAlt = mods.has('alt') || mods.has('option');

  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad/.test(navigator.platform || '');

  if (wantMod) {
    if (isMac ? !e.metaKey : !e.ctrlKey) return false;
  } else {
    if (wantCtrl && !e.ctrlKey) return false;
    if (wantMeta && !e.metaKey) return false;
    if (!wantCtrl && e.ctrlKey && !wantMod) return false;
    if (!wantMeta && e.metaKey && !wantMod) return false;
  }
  if (wantShift !== e.shiftKey) return false;
  if (wantAlt !== e.altKey) return false;

  return e.key.toLowerCase() === key;
}

export function ActionBar({
  actions,
  approvalId,
  onDecide,
  disabled = false,
  className,
}: ActionBarProps) {
  const [pendingConfirm, setPendingConfirm] = React.useState<Action | null>(
    null,
  );

  const fireRef = React.useRef<(a: Action) => void>(() => {});
  fireRef.current = (action: Action) => {
    if (disabled) return;
    if (action.confirmation) {
      setPendingConfirm(action);
    } else {
      void onDecide(action);
    }
  };

  // Keyboard shortcuts — bind once and re-dispatch via ref so we don't have
  // to re-attach the listener every render when `actions` changes identity.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't intercept typing inside form controls.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        if (
          tag === 'input' ||
          tag === 'textarea' ||
          tag === 'select' ||
          target.isContentEditable
        ) {
          // Allow mod+enter to escape the textarea/input — common submit shortcut.
          if (!(e.key === 'Enter' && (e.metaKey || e.ctrlKey))) return;
        }
      }
      for (const a of actions) {
        if (a.shortcut && matchShortcut(a.shortcut, e)) {
          e.preventDefault();
          fireRef.current(a);
          break;
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  return (
    <>
      <footer
        className={cn(
          'flex flex-wrap items-center gap-2 border-t border-border/60 bg-muted/30 px-4 py-3',
          className,
        )}
        data-action-bar="true"
        data-approval-id={approvalId}
      >
        {actions.map((action) => (
          <Button
            key={action.id}
            type="button"
            variant={buttonVariantFor(action.variant)}
            disabled={disabled}
            onClick={() => fireRef.current(action)}
            title={
              action.shortcut
                ? `${action.label} (${action.shortcut})`
                : action.label
            }
            data-action-id={action.id}
            data-action-decision={action.decision}
          >
            {action.label}
            {action.shortcut && (
              <span
                className="ml-2 hidden text-[10px] text-muted-foreground sm:inline"
                aria-hidden="true"
              >
                {action.shortcut}
              </span>
            )}
          </Button>
        ))}
      </footer>

      {/* Confirmation dialog — fires only when the chosen Action declares
          one. Decoupled from the bar render so the dialog can be a Radix
          portal without re-rendering the buttons. */}
      <Dialog
        open={pendingConfirm !== null}
        onOpenChange={(o) => {
          if (!o) setPendingConfirm(null);
        }}
      >
        <DialogContent>
          {pendingConfirm?.confirmation && (
            <>
              <DialogHeader>
                <DialogTitle>{pendingConfirm.confirmation.title}</DialogTitle>
                <DialogDescription>
                  {pendingConfirm.confirmation.body}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPendingConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant={buttonVariantFor(pendingConfirm.variant)}
                  onClick={() => {
                    const a = pendingConfirm;
                    setPendingConfirm(null);
                    if (a) void onDecide(a);
                  }}
                >
                  {pendingConfirm.confirmation.confirmLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
